import React from 'react';

const PrivacyPolicy = () => {
  // Brand Color defined for consistency references: #1e4d91
  
  return (
    <div className="min-h-screen pt-28 pb-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-6xl mx-auto bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-100">
        
        {/* Header Section - Uses Main Brand Color */}
        <div className="bg-[#1e4d91] px-6 py-12 sm:px-12 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-3">
              Privacy Policy
            </h1>
            <p className="text-blue-100 text-lg sm:text-xl font-medium">
              Subramaniam Financial Services
            </p>
            <div className="w-24 h-1.5 bg-white/30 mx-auto mt-6 rounded-full"></div>
          </div>
          {/* Decorative Pattern Overlay */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        </div>

        {/* Content Container */}
        <div className="px-5 py-10 sm:px-12 space-y-12 text-gray-700 leading-relaxed">

          {/* 1. Introduction */}
          <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">1</span>
              Introduction
            </h2>
            <p className="mb-4 text-justify text-base sm:text-lg">
              Subramaniam Financial Services values your trust and is committed to protecting your privacy and personal information. As a Direct Selling Agent (DSA) for leading banks and Non-Banking Financial Companies (NBFCs), Subramaniam Financial Services provides ethical, transparent, and reliable financial services tailored to customers’ needs.
            </p>
            <p className="mb-4 text-justify text-base sm:text-lg">
              This Privacy Policy explains how Subramaniam Financial Services collects, uses, stores, and safeguards personal and financial information when you interact with our website, advertisements, services, or communication channels. All data is handled responsibly and in compliance with applicable laws, regulatory requirements, and industry standards.
            </p>
            <p className="font-semibold text-[#1e4d91]">
              By using our services or submitting your information to Subramaniam Financial Services, you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>
          </section>

          {/* 2. Information We Collect */}
          <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">2</span>
              Information We Collect
            </h2>
            <p className="mb-6 text-justify text-base sm:text-lg">
              When you use the services of Subramaniam Financial Services or submit an enquiry through our website, advertisements, or other communication channels, we may collect the following information to provide ethical, transparent, and reliable financial assistance:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-[#1e4d91] shadow-sm hover:shadow-md transition-shadow">
                <h3 className="font-bold text-[#1e4d91] text-lg mb-3">Personal Information:</h3>
                <ul className="space-y-2 text-sm sm:text-base">
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Full Name</li>
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Mobile Number</li>
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Email Address</li>
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Residential Address</li>
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Date of Birth (if required)</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-[#1e4d91] shadow-sm hover:shadow-md transition-shadow">
                <h3 className="font-bold text-[#1e4d91] text-lg mb-3">Financial Information:</h3>
                <ul className="space-y-2 text-sm sm:text-base">
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Income details</li>
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Employment or business information</li>
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Loan or financial product requirements</li>
                  <li className="flex items-start"><span className="text-[#1e4d91] mr-2">•</span> Credit-related information required for eligibility assessment</li>
                </ul>
              </div>
            </div>

            <div className="mb-6 bg-white p-4 border border-gray-200 rounded-lg">
              <h3 className="font-bold text-[#1e4d91] mb-2">KYC & Identity Information:</h3>
              <ul className="list-disc pl-5 space-y-1 mb-2 text-sm sm:text-base marker:text-[#1e4d91]">
                <li>PAN Card details</li>
                <li>Aadhaar details (where applicable and as permitted by law)</li>
                <li>Other identification documents required by banks or NBFCs</li>
              </ul>
              <p className="text-sm text-gray-500 italic border-t border-gray-100 pt-2 mt-2">KYC information is collected strictly for verification and regulatory compliance purposes.</p>
            </div>

            <div className="mb-6 bg-white p-4 border border-gray-200 rounded-lg">
              <h3 className="font-bold text-[#1e4d91] mb-2">Communication Information:</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm sm:text-base marker:text-[#1e4d91]">
                <li>Call recordings, messages, emails, or enquiry details</li>
                <li>Information shared during customer support or follow-up interactions.</li>
              </ul>
            </div>

            {/* Highlight Box */}
            <div className="bg-[#1e4d91]/5 border-l-4 border-[#1e4d91] p-5 rounded-r-lg">
              <h4 className="font-bold text-[#1e4d91] mb-2">Data Collection & Usage Note:</h4>
              <p className="text-sm sm:text-base text-gray-700 text-justify">
                Subramaniam Financial Services collects customer information solely for the purpose of processing financial service requests and coordinating with authorized banks and NBFC partners. We do not sell, rent, or misuse personal data and ensure that all information is handled securely, confidentially, and in accordance with applicable laws and regulatory requirements.
              </p>
              <p className="text-sm sm:text-base text-gray-700 text-justify">
                Information collected through advertisements, online forms, or digital platforms is used only to assist customers with financial services and to meet legal and regulatory compliance obligations.
              </p>
            </div>
          </section>

          {/* 3. How We Use Your Information */}
          <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">3</span>
              How We Use Your Information
            </h2>
            <p className="mb-4 text-base sm:text-lg">Customer information collected by Subramaniam Financial Services is used strictly for lawful, transparent, and service-related purposes, including:</p>
            <ul className="space-y-4 mb-4">
              {[
                { title: "Loan Eligibility Assessment", desc: "To evaluate the customer’s eligibility based on applicable bank and NBFC lending criteria." },
                { title: "Application Processing & Coordination", desc: "To submit, process, and coordinate loan applications with authorized Banks and NBFC partners." },
                { title: "Customer Support & Communication", desc: "To provide timely updates, clarifications, and assistance throughout the loan application journey." },
                { title: "Legal, Regulatory & Compliance Requirements", desc: "To comply with applicable laws, regulatory guidelines, internal audits, and KYC norms mandated by financial institutions." }
              ].map((item, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="bg-[#1e4d91] rounded-full p-1 mt-1 mr-3 flex-shrink-0">
                     <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span className="text-base sm:text-lg"><strong className="text-[#1e4d91]">{item.title}</strong> – {item.desc}</span>
                </li>
              ))}
            </ul>
            <p className="mb-4 text-base sm:text-lg">All customer data is handled with strict confidentiality and is used only for the purposes stated above, in accordance with applicable data protection, privacy standards, and regulatory requirements.</p>
          </section>

          {/* 4. Information Sharing & Disclosure */}
          <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">4</span>
              Information Sharing & Disclosure
            </h2>
            <p className="mb-4 text-justify text-base sm:text-lg">
              Subramaniam Financial Services respects the confidentiality of customer information and shares personal and financial data only when necessary and in accordance with applicable laws and regulations. We may share customer information in the following circumstances:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-6 text-base sm:text-lg marker:text-[#1e4d91]">
              <li>With authorized banks and Non-Banking Financial Companies (NBFCs) for the purpose of processing loan applications and providing financial services</li>
              <li>With service providers or partners who assist us in operating our business, subject to strict confidentiality obligations</li>
              <li>When required to comply with legal, regulatory, or governmental requirements</li>
              <li>To protect our rights, customers, or the integrity of our services</li>
            </ul>
            <p className="font-semibold text-[#1e4d91] mb-4 text-base sm:text-lg">
              Subramaniam Financial Services does not sell, rent, or trade customer personal or financial information to third-party marketers or unauthorized entities.
            </p>
            
            <div className="bg-orange-50 border border-orange-200 p-5 rounded-lg">
              <h4 className="font-bold text-orange-800 mb-1 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                Third-Party Responsibility Notice:
              </h4>
              <p className="text-sm sm:text-base text-orange-900 text-justify mt-1">
                While we take reasonable measures to ensure that our partners follow appropriate data protection practices, Subramaniam Financial Services is not responsible for the privacy practices of banks, NBFCs, or third-party websites that have their own independent privacy policies.
              </p>
            </div>
          </section>

          {/* 5. Data Security */}
          <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">5</span>
              Data Security
            </h2>
            <p className="mb-4 text-justify text-base sm:text-lg">
              The information collected by Subramaniam Financial Services is used solely for providing financial services in a lawful, ethical, and transparent manner. Customer information may be used for the following purposes:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4 text-base sm:text-lg marker:text-[#1e4d91]">
              <li>To evaluate eligibility for financial products and services</li>
              <li>To process, manage, and submit applications with authorized banks and NBFC partners</li>
              <li>To complete identity verification and KYC requirements</li>
              <li>To communicate with customers regarding enquiries, application status, approvals, or rejections</li>
              <li>To provide customer support and resolve service-related issues</li>
              <li>To improve service quality, internal processes, and customer experience</li>
              <li>To comply with legal, regulatory, and audit obligations</li>
            </ul>
            <p className="mb-4 text-justify text-base sm:text-lg">
              Subramaniam Financial Services ensures that customer data is used only for the purposes stated above and is not used for any misleading, unauthorized, or unlawful activities.
            </p>
          </section>

          {/* 6. Cookies & Website Tracking */}
          {/* <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">6</span>
              Cookies & Website Tracking
            </h2>
            <p className="mb-4 text-justify text-base sm:text-lg">
              Subramaniam Financial Services uses cookies and similar tracking technologies to improve website functionality, enhance user experience, and analyze website performance. Cookies are small data files stored on your device when you visit our website.
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-4 text-base sm:text-lg marker:text-[#1e4d91]">
              <li>Understand how users interact with our website</li>
              <li>Improve website content, performance, and usability</li>
              <li>Analyze traffic and user behavior through analytics tools</li>
              <li>Remember user preferences for a better browsing experience</li>
            </ul>
          </section> */}

          {/* 7. User Rights */}
          <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">6</span>
              User Rights
            </h2>
            <p className="mb-4 text-base sm:text-lg">
              Subramaniam Financial Services respects the rights of users regarding their personal and financial information. Customers have the following rights, subject to applicable laws and regulatory requirements:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4 text-base sm:text-lg marker:text-[#1e4d91]">
              <li>The right to access the personal information we hold about them</li>
              <li>The right to request correction or updating of inaccurate or incomplete information</li>
              <li>The right to request deletion of personal information, subject to legal, regulatory, or contractual obligations</li>
              <li>The right to withdraw consent for data processing, where applicable</li>
              <li>The right to raise concerns or complaints regarding the use of their personal information</li>
            </ul>
            <p className="mb-4 text-base sm:text-lg">
              To exercise any of the above rights, users may contact Subramaniam Financial Services using the contact details provided in this Privacy Policy. Requests will be reviewed and addressed in accordance with applicable laws and regulatory guidelines.
            </p>
          </section>

          {/* 8. Third-Party Links */}
          <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">7</span>
              Third-Party Links
            </h2>
            <p className="text-justify text-base sm:text-lg">
              The website of Subramaniam Financial Services may contain links to third-party websites, including those of banks, NBFCs, or other service providers. These external websites are governed by their own privacy policies and practices. Subramaniam Financial Services does not have control over and is not responsible for the content, privacy practices, or security measures of third-party websites. Users are encouraged to review the privacy policies of any third-party sites they visit through links provided on our website.
            </p>
          </section>

          {/* 9. Policy Updates */}
          <section>
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-5 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">8</span>
              Policy Updates
            </h2>
            <p className="text-justify text-base sm:text-lg">
              Subramaniam Financial Services may update or modify this Privacy Policy from time to time to reflect changes in legal, regulatory, or operational requirements. Any updates will be posted on this page, and the revised policy will be effective from the date of publication. Users are encouraged to review this Privacy Policy periodically to stay informed about how their information is protected and used.
            </p>
          </section>

          {/* 10. Contact Us */}
          <section className="mt-16">
            <h2 className="flex items-center text-xl sm:text-2xl font-bold text-[#1e4d91] mb-6 border-b pb-3 border-gray-200">
              <span className="bg-[#1e4d91]/10 text-[#1e4d91] text-sm font-extrabold px-3 py-1 rounded-full mr-3 border border-[#1e4d91]/20">9</span>
              Contact Us
            </h2>
            <p className="mb-6 text-base sm:text-lg">
              If you have any questions, concerns, or requests regarding this Privacy Policy or the use of your personal information, you may contact Subramaniam Financial Services using the details provided below:
            </p>
            
            {/* Contact Card */}
            <div className="bg-[#1e4d91] text-white rounded-xl p-5 sm:p-8 shadow-lg overflow-hidden relative">
              {/* Subtle background decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>

              <div className="grid lg:grid-cols-2 gap-8 relative z-10">
                
                {/* Left Column: Phone & Email */}
                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-start">
                     <div className="bg-white/10 p-2.5 rounded-lg mr-4 shrink-0">
                        <svg className="w-5 h-5 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                     </div>
                     <div className="min-w-0 flex-1">
                       <p className="text-blue-200 text-xs font-medium uppercase tracking-wider mb-1">Phone</p>
                       <a href="tel:8973739377" className="text-lg font-semibold text-white hover:text-blue-200 transition-colors block">
                         89737 39377
                       </a>
                     </div>
                  </div>

                  {/* Email - FIXED overflow with break-all */}
                  <div className="flex items-start">
                     <div className="bg-white/10 p-2.5 rounded-lg mr-4 shrink-0">
                        <svg className="w-5 h-5 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                     </div>
                     <div className="min-w-0 flex-1">
                       <p className="text-blue-200 text-xs font-medium uppercase tracking-wider mb-1">Email</p>
                       <a href="mailto:subramaniamfinancialservices@gmail.com" className="text-base font-medium text-white hover:text-blue-200 transition-colors break-all leading-snug block">
                         subramaniamfinancialservices@gmail.com
                       </a>
                     </div>
                  </div>
                </div>

                {/* Right Column: Address - FIXED wrapping */}
                <div className="flex items-start">
                   <div className="bg-white/10 p-2.5 rounded-lg mr-4 shrink-0">
                      <svg className="w-5 h-5 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                   </div>
                   <div className="min-w-0 flex-1">
                     <p className="text-blue-200 text-xs font-medium uppercase tracking-wider mb-1">Office Address</p>
                     <address className="not-italic text-base text-white/90 leading-relaxed font-normal break-words">
                       137/C, K.P.R Thevar Complex, North Car Street,<br className="hidden sm:block"/> Srivilliputtur – 626125, Tamil Nadu, India.
                     </address>
                   </div>
                </div>

              </div>
            </div>
            <p className="mt-8 text-sm text-gray-500 text-center">
              We will make reasonable efforts to respond to your queries and address your concerns in a timely manner.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
